/**
 * Centralized Authentication Utilities
 * 
 * Provides global logout and modal-based authentication functions
 * used by QueryClient and manual actions throughout the app.
 */

import { QueryClient } from '@tanstack/react-query';
import { authApi } from './api/auth';

let isLoggingOut = false; // Prevent duplicate logout calls

/**
 * Global logout function
 * 
 * Called by:
 * - QueryClient when any API returns 401
 * - AuthContext.logout() for manual logout
 * - Any component needing to force logout
 * 
 * Flow:
 * 1. Prevent duplicate calls with flag
 * 2. Call logout API to clear server-side HttpOnly cookie
 * 3. Clear all query cache data
 * 4. Navigate to home page (modal will handle re-authentication if needed)
 */
export async function logout(queryClient: QueryClient) {
  if (isLoggingOut) {
    console.log('[Auth] Logout already in progress, ignoring duplicate call');
    return;
  }
  
  isLoggingOut = true;
  console.log('[Auth] Starting logout flow');

  try {
    // Best-effort server-side logout to clear HttpOnly cookie
    await authApi.logout();
    console.log('[Auth] Server-side logout successful');
  } catch (error) {
    // Ignore errors here - we want to logout client-side even if API call fails
    console.warn('[Auth] Server-side logout failed (continuing client-side logout):', error);
  }

  // Clear all cached queries (removes stale user data)
  queryClient.clear();
  console.log('[Auth] Query cache cleared');

  // Reset flag before navigation
  isLoggingOut = false;

  // Navigate to home page instead of login page
  // The modal system will handle authentication when user tries to access protected content
  window.location.assign('/');
}

/**
 * Modal-aware authentication handler for 401 errors
 * 
 * Used by QueryClient error handler to trigger login modal
 * instead of full page navigation when appropriate.
 */
export function handleAuthenticationRequired(
  currentPath: string,
  openLoginModal?: (redirectTo: string) => void
): void {
  // Always prefer modal if available
  if (openLoginModal) {
    console.log('[Auth] 401 detected, showing login modal');
    openLoginModal(currentPath);
  } else {
    // Fallback to home page navigation - let the protected route components handle auth
    console.log('[Auth] 401 detected, navigating to home page (no modal available)');
    window.location.assign('/');
  }
}
